import React, { type FunctionComponent } from "react";
import { Calendar as BigCalendar, type CalendarProps as BigCalendarProps, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-dates/initialize";
import { graphql, type RelayRefetchProp } from "react-relay";
import { type RouteComponentProps, withRouter } from "react-router-dom";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { getHours, getMinutes, set, startOfMinute } from "date-fns";
import { delay, isEqual, noop } from "lodash";
import moment, { type Moment } from "moment";
import {
	commitLocalUpdate,
	commitMutation,
	ConnectionHandler,
	fetchQuery,
	type RecordSourceProxy,
} from "relay-runtime";
import { Popup } from "semantic-ui-react";

import type { EventFormResult } from "~/calendar/EventForm";
import { saveEventMutation } from "~/calendar/hooks/useSaveEventMutation";
import dismissElement from "~/common/DismissibleElements";
import ViewportContext from "~/common/ViewportContext";
import { urlsafeB64Encode } from "~/lib/base64";
import { inGoogleMeet } from "~/lib/browserExtension";
import { choiceModal } from "~/lib/choiceModal";
import { DailyUpdater } from "~/lib/dailyUpdate";
import { debugAssert } from "~/lib/log";
import perf from "~/lib/perf";
import recaptcha from "~/lib/recaptcha";
import { decodeGlobalId, LRURetainer, smart } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { Twix } from "~/lib/twix";
import { inCalendarMode, inVideocallMode, inZoomMeeting, isZoom, type NodesFromEdges } from "~/lib/utils";
import WebsocketClient, { GroupEmitter } from "~/lib/websocket";
import zoomSdk, { configureZoom } from "~/lib/zoom";
import { environment } from "~/models";
import { DayEventContent } from "~/nav/panels/Meetings/DayViewEvent";
import { AgendaView, DayView, TimeSlotWrapper } from "~/nav/panels/Meetings/Events";
import ResyncCalendar from "~/nav/panels/Meetings/ResyncCalendar";
import {
	isViewMode,
	PAGE_SIZE,
	meetingsStore as store,
	VIEW_MODE_KEY,
	type ViewMode,
} from "~/nav/panels/Meetings/utils";
import type { URLParams } from "~/stream/StreamHeader/StreamHeader";
import { Button } from "~/ui";
import { __globalModalContext } from "~/ui/modals";
import toast from "~/ui/toast";

// This import has the side effect of starting a DailyUpdater to fetch the meetings on the current date on date change
import "./backgroundMeetingsPrefetch";
import EventFormModal from "./EventFormModal/EventFormModal";
import MeetingsPanelHeader from "./MeetingsPanelHeader";
import OpenWeekViewButton from "./OpenWeekViewButton";
import PanelMessage from "./PanelMessage/PanelMessage";

import type { MeetingsPanel_viewer$data as Viewer } from "~/__generated__/MeetingsPanel_viewer.graphql";
import type {
	EventEditType,
	MeetingsPanelAttendanceMutation,
	ResponseStatusChoices,
} from "~/__generated__/MeetingsPanelAttendanceMutation.graphql";
import ConcreteMeetingsPanelEventQuery, {
	type MeetingsPanelEventQuery as MeetingsPanelEventQueryOperation,
} from "~/__generated__/MeetingsPanelEventQuery.graphql";
import ConcreteMeetingsPanelQuery, {
	type MeetingsPanelQuery$data,
	type MeetingsPanelQuery as MeetingsPanelQueryOperation,
} from "~/__generated__/MeetingsPanelQuery.graphql";
import type { useSaveEventMutationMutation } from "~/__generated__/useSaveEventMutationMutation.graphql";

import styles from "~/nav/panels/Meetings/MeetingsPanel.global.module.css";
import panelStyles from "~/ui/CollapsiblePanel/CollapsiblePanel.module.css";

const localizer = momentLocalizer(moment);

export type ConferenceCall = NodesFromEdges<
	MeetingsPanelQueryOperation["response"]["viewer"]["dayEvents"]
>["conferenceCalls"][0];
export type Calendar = NodesFromEdges<Viewer["calendars"]>;

const ZOOM_TOOLTIP = "Meetings__zoomCreateEvent";

export type DayEvent = NodesFromEdges<MeetingsPanelQuery$data["viewer"]["dayEvents"]>;

export type WebSocketSignalData = {
	type: string;
	data: {
		calendarId: number;
		events?: {
			[eventGuid: string]: Partial<DayEvent>;
		};
	};
};

export const MeetingsPanelQuery = graphql`
	query MeetingsPanelQuery($end: DateTime, $start: DateTime) {
		viewer {
			dayEvents: eventsByUserEventFilters(start: $start, end: $end) {
				edges {
					node {
						id
						start
						end
						title
						guid
						type
						calendar {
							id
							source
						}
						status
						conferenceCalls {
							provider
							url
							phoneNumber
							pin
						}
						location
						recurrenceRule
						userResponseStatus
						isHidden
						isOrganizer
						isAllDay
						color
						timeBlockType
						hasNoteContent
						hasUnreadNoteContent
						attendeePersonCount
						noteFinished
						note {
							stream {
								id
								hasExternalUsersOrHasRelatedExternalStreams
							}
						}
						willBeRecorded
						...RecordIcon_event
					}
				}
			}
		}
	}
`;

const MeetingsPanelEventQuery = graphql`
	query MeetingsPanelEventQuery($eventGuid: String) {
		event: eventByGuid(guid: $eventGuid) {
			... on Event {
				id
				start
			}
		}
	}
`;

export const attendanceMutation = graphql`
	mutation MeetingsPanelAttendanceMutation(
		$calendarId: ID!
		$guid: String!
		$userResponseStatus: ResponseStatusChoices!
		$editType: EventEditType
	) {
		eventAttendance(
			calendarId: $calendarId
			guid: $guid
			userResponseStatus: $userResponseStatus
			editType: $editType
		) {
			event {
				id
				guid
			}
		}
	}
`;

interface AgendaProps extends RouteComponentProps<URLParams> {
	viewer: Viewer;
	resyncOnMount: boolean;
	visible: boolean;
	relay?: RelayRefetchProp;
	onEventsChanged?: () => void;
	createNewMeeting?: () => void;
	isTourOngoing?: boolean;
}

interface AgendaState {
	now: Moment;
	start: Moment;
	end: Moment;
	current: Moment;
	dropdownDate: Moment;
	focused: boolean;
	scrollToMeeting: boolean;
	viewMode: ViewMode;
	lastSynced: string;
	isResyncing: boolean;
	loading: boolean;
	prevLoading: boolean;
	nextLoading: boolean;
	prevButtonDisplayed: boolean;
	nextButtonDisplayed: boolean;
	dayEvents: DayEvent[];
	eventAttendanceMutationLoading: boolean;

	selectedIntervalStart: Moment | undefined;
	selectedIntervalEnd: Moment | undefined;

	zoomCreateEventTooltipOpen: boolean;
}

const agendaContextDefault = {
	refreshEvents: noop,
	calendars: [],
	openEditModal: noop,
	answerEventAttendance: noop,
	answerEventAttendanceLoading: false,
	currentDate: new Date(),
	dropdownDate: new Date(),
	visible: true,
	loading: false,
	prevLoading: false,
	nextLoading: false,
	prevButtonDisplayed: false,
	nextButtonDisplayed: false,
	setPrevButtonDisplayed: noop,
	setNextButtonDisplayed: noop,
	onTopScrolled: noop,
	onBottomScrolled: noop,
	onDateScrolled: noop,
	isDarkMode: false,
	uxSimplificationEnabled: false,
};

export type AgendaContextType = {
	refreshEvents: () => void;
	calendars: Calendar[];
	openEditModal: (eventGuid: string) => void;
	answerEventAttendance: (
		calendar: Calendar,
		eventGuid: string,
		userResponseStatus: ResponseStatusChoices,
		callback?: () => void,
	) => void;
	answerEventAttendanceLoading: boolean;
	currentDate: Date;
	dropdownDate: Date;
	visible: boolean;
	loading: boolean;
	prevLoading: boolean;
	nextLoading: boolean;
	prevButtonDisplayed: boolean;
	nextButtonDisplayed: boolean;
	setPrevButtonDisplayed: (val: boolean) => void;
	setNextButtonDisplayed: (val: boolean) => void;
	onTopScrolled: () => void;
	onBottomScrolled: () => void;
	onDateScrolled: (date: Moment) => void;
	context?: React.ContextType<typeof ViewportContext>;
	uxSimplificationEnabled: boolean;
};

export const AgendaContext = React.createContext<AgendaContextType>(agendaContextDefault);
AgendaContext.displayName = "AgendaContext";
const queryRetainer = new LRURetainer(10);

const closeGoogleCalendarPanel = () => {
	window.parent.postMessage(
		{
			type: "EXTENSION_CLOSE_SIDE_PANEL",
		},
		"*",
	);
};

type ObserveKeys = "MeetingsPanel" | "HomePanel" | "Calendar";
type ObserveCallback = (ev: WebSocketSignalData["data"]["events"]) => void;

export class CalendarObserver {
	private static instance?: CalendarObserver;

	static getInstance() {
		if (this.instance) return this.instance;
		return (this.instance = new this());
	}

	private emitters = new Map<string, GroupEmitter>();
	private observers: { [k in ObserveKeys]?: ObserveCallback };
	private refetchNeeded = false;

	constructor() {
		debugAssert(
			!CalendarObserver.instance,
			"CalendarObserver is a singleton; it's recommended to use CalendarObserver.getInstance()",
		);
		this.observers = {};
	}

	addCalendar(id: string): void {
		const existingEmitter = this.emitters.get(id);
		if (existingEmitter) {
			if (existingEmitter.online) {
				return;
			} else {
				this.unregisterEmitter(id, existingEmitter);
			}
		}
		const newEmitter = WebsocketClient.getInstance().getOrJoinGroup(`calendars.${id}`);
		this.registerEmitter(id, newEmitter);
	}

	removeCalendar(id: string): void {
		const emitter = this.emitters.get(id);
		if (emitter) {
			this.unregisterEmitter(id, emitter);
		}
	}

	observe(name: ObserveKeys, notify: ObserveCallback): { refetchNeeded: boolean } {
		debugAssert(!this.observers[name], "Clobbering CalendarObserver observer (missing `unobserve()` call?)");
		this.observers[name] = notify;

		// FIXME: I don't like this one bit
		if (name === "MeetingsPanel") {
			const { refetchNeeded } = this;
			this.refetchNeeded = false;
			return { refetchNeeded };
		}
		return { refetchNeeded: false };
	}

	unobserve(name: ObserveKeys): void {
		delete this.observers[name];
	}

	updateCalendarEvent(
		calendar: string,
		eventGuid: string,
		updates: Partial<DayEvent>,
		store?: RecordSourceProxy,
	): void {
		const [, _calendarId] = decodeGlobalId(calendar);
		const calendarId = parseInt(_calendarId, 10);

		const data: WebSocketSignalData = {
			type: "CALENDAR_UPDATED",
			data: { calendarId, events: { [eventGuid]: updates } },
		};
		this.onCalendarGroupMessage(data, store);
	}

	private _notifyObservers(data: WebSocketSignalData["data"]["events"] | undefined) {
		for (const observer of Object.values(this.observers)) {
			observer?.(data);
		}
	}

	private registerEmitter(id: string, emitter: GroupEmitter): void {
		this.emitters.set(id, emitter);
		emitter.on("message", this.onCalendarGroupMessage);
	}

	private unregisterEmitter(id: string, emitter: GroupEmitter): void {
		this.emitters.delete(id);
		emitter.off("message", this.onCalendarGroupMessage);
		emitter.close();
	}

	private onCalendarGroupMessage = (data: WebSocketSignalData, store?: RecordSourceProxy) => {
		if (data.type === "CALENDAR_UPDATED") {
			const { calendarId, events } = data.data;
			if (!events) {
				this._notifyObservers(events);
				if (!this.observers.MeetingsPanel) {
					this.refetchNeeded = true;
				}
				return;
			}

			function updater(store: RecordSourceProxy) {
				if (!events) return;

				// Can't even get the event's relay ID without the calendar ID so we'll
				// just give up now.
				if (typeof calendarId !== "number") return;

				const viewer = store.get("viewer");
				const dayEvents = viewer ? ConnectionHandler.getConnection(viewer, "HomePanel_dayEvents") : undefined;
				const cancelledEvents = [];

				for (const [guid, updates] of Object.entries(events)) {
					const eventRelayId = urlsafeB64Encode(`Event:${calendarId}__CID__${guid}`);
					if (updates.status === "cancelled") {
						cancelledEvents.push(eventRelayId);
					}

					const event = store.get<(typeof events)[string]>(eventRelayId);
					if (!event) continue;

					for (const [key, value] of Object.entries(updates)) {
						// This is needed because some updates might not be primitive
						// values. Even though JSON is a scalar type in graphql, relay
						// doesn't allow us to pass an object to RecordProxy.setValue.
						/* eslint-disable no-underscore-dangle */
						// @ts-ignore
						event._mutator.setValue(event._dataID, key, value);
						/* eslint-enable no-underscore-dangle */
					}
				}

				if (dayEvents) {
					cancelledEvents.forEach(id => {
						ConnectionHandler.deleteNode(dayEvents, id);
					});
				}
			}

			if (store) {
				updater(store);
			} else {
				commitLocalUpdate(environment, updater);
			}

			this._notifyObservers(events);
		}
	};
}

const EventCalendar = React.memo(function EventCalendar({
	context,
	className,
	...props
}: BigCalendarProps<DayEvent> & {
	context: AgendaContextType;
}) {
	return (
		<AgendaContext.Provider value={context}>
			<BigCalendar<DayEvent> {...props} className={cx(className, styles.rbcOverrides)} />
		</AgendaContext.Provider>
	);
});

export default withRouter(
	smart(
		class Agenda extends React.Component<AgendaProps, AgendaState> {
			static Fragments = {
				viewer: graphql`
					fragment MeetingsPanel_viewer on Viewer {
						uxSimplificationEnabled
						calendarServiceToReconnect
						dismissedElements {
							id
							key
						}
						hasAccessToCopilot
						...EventsCalendars @relay(mask: false)
						...EventFormModal_viewer
						...MeetingsPanelHeader_viewer
					}
				`,
			};

			static Refetch = graphql`
				query MeetingsPanelLastSyncedQuery {
					viewer {
						...MeetingsPanel_viewer
					}
				}
			`;

			static contextType = ViewportContext;
			declare context: React.ContextType<typeof ViewportContext>;

			resyncRef: { onResyncClicked?: () => void } = {};

			refreshTimeout: number | null = null;

			dailyUpdater = new DailyUpdater(() => {
				this.onDateToday();
			});

			constructor(props: AgendaProps) {
				super(props);

				let viewMode: ViewMode;
				if (inVideocallMode || inCalendarMode) {
					viewMode = "day";
				} else if (props.isTourOngoing) {
					viewMode = "agenda";
				} else {
					const storeViewMode = store.get(VIEW_MODE_KEY, "agenda");
					viewMode = isViewMode(storeViewMode) ? storeViewMode : "agenda";
				}

				const isDay = viewMode === "day";
				const current = moment().startOf("day");
				const start = isDay ? moment(current).startOf("day") : moment(current).subtract(PAGE_SIZE, "day");
				const end = isDay ? moment(current).endOf("day") : moment(current).add(PAGE_SIZE, "day");
				const eventGuid = this.getEventGuid();

				this.state = {
					now: moment(),
					start,
					end,
					current,
					dropdownDate: current,
					focused: false,
					scrollToMeeting: true,
					lastSynced: "",
					isResyncing: false,
					viewMode,
					loading: false,
					prevLoading: false,
					nextLoading: false,
					prevButtonDisplayed: false,
					nextButtonDisplayed: false,
					dayEvents: [],
					eventAttendanceMutationLoading: false,
					selectedIntervalStart: undefined,
					selectedIntervalEnd: undefined,
					zoomCreateEventTooltipOpen: false,
				};

				if (!this.props.viewer.dismissedElements.some(e => e.key === ZOOM_TOOLTIP) && inZoomMeeting && !eventGuid) {
					// Controlled Popup gets misplaced if set to open on page load
					window.setTimeout(this.openZoomOnboardingPopup, 200);
				}
				if (isZoom) {
					configureZoom();
				}
			}

			componentDidMount() {
				// Re-render every 60 seconds to update the "now" indicator.
				// RBC does this already, but a bug prevents the current hour from updating.
				// See https://github.com/intljusticemission/react-big-calendar/issues/636
				this.refreshTimeout = window.setInterval(this.updateNow, 60000);
				const lastSynced = this.getLastSyncDatetime();

				this.dailyUpdater.start();

				perf.sidebarLoaded();

				// Listen for changes from the server on the user's calendars
				const { calendars } = this.props.viewer;
				calendars?.edges?.forEach(edge => {
					const id = edge?.node?.id.split("=").join("");
					if (id) {
						CalendarObserver.getInstance().addCalendar(id);
					}
				});

				const { refetchNeeded } = CalendarObserver.getInstance().observe("MeetingsPanel", this.onCalendarMessage);

				const hadSyncErrors = calendars?.edges?.some(edge => edge?.node?.hadErrorOnSync);

				if (this.props.resyncOnMount && (!lastSynced || hadSyncErrors)) {
					delay(() => this.resyncRef.onResyncClicked?.(), 2000);
				}

				const eventGuid = this.getEventGuid();
				// We should wait for the current event data before loading the day events
				if (!eventGuid) {
					this.setState({ lastSynced, loading: true }, () => this.loadEvents());
					return;
				}
				// Load current event and then load the day events
				const variables = { eventGuid };
				queryRetainer.retain(ConcreteMeetingsPanelEventQuery, variables);

				const afterStateInit = () => {
					const p = this.loadEvents();
					// Refresh right away which sounds bad, but keep in mind that
					// loadEvents will be using the relay cache so it usually won't be
					// doing a network request anyway.
					if (refetchNeeded) p.then(() => this.refreshEvents());
				};

				fetchQuery<MeetingsPanelEventQueryOperation>(environment, MeetingsPanelEventQuery, variables, {
					fetchPolicy: "store-or-network",
				})
					.toPromise()
					.then(response => {
						if (!response?.event) {
							this.setState({ lastSynced, loading: true }, afterStateInit);
							return;
						}
						const viewMode = inVideocallMode || inCalendarMode ? "day" : store.get(VIEW_MODE_KEY, "agenda");
						const isDay = viewMode === "day";
						const current = moment(response.event.start).startOf("day");
						const start = isDay ? moment(current).startOf("day") : moment(current).subtract(PAGE_SIZE, "day");
						const end = isDay ? moment(current).endOf("day") : moment(current).add(PAGE_SIZE, "day");
						this.setState({
							current,
							start,
							end,
							dropdownDate: moment(current),
						});
						this.setState({ lastSynced, loading: true }, afterStateInit);
					})
					.catch(() => {
						this.setState({ lastSynced, loading: true }, afterStateInit);
					});
			}

			componentWillUnmount() {
				if (this.refreshTimeout) window.clearInterval(this.refreshTimeout);
				this.refreshTimeout = null;
				CalendarObserver.getInstance().unobserve("MeetingsPanel");
				this.dailyUpdater.stop();
			}

			static getDerivedStateFromProps(nextProps: AgendaProps, prevState: AgendaState) {
				// Whenever isTourOngoing becomes true, update viewMode to "agenda"
				if (nextProps.isTourOngoing && prevState.viewMode !== "agenda") {
					return {
						viewMode: "agenda",
					};
				}
				return null;
			}

			getEventGuid = (): string | null => {
				const guidMatch = /\/meetings\/(.*?)\//gi.exec(this.props.location.pathname);
				return guidMatch && guidMatch.length >= 2 ? guidMatch[1] : null;
			};

			updateNow = () => {
				this.setState({ now: moment() });
			};

			onCalendarMessage = (newEvents: WebSocketSignalData["data"]["events"]) => {
				if (newEvents) {
					this.setState(({ dayEvents }) => ({
						dayEvents: dayEvents.map(event => ({
							...event,
							...(newEvents[event.guid] ?? {}),
						})),
					}));
				} else if (!this.state.isResyncing) {
					// If we are currently resycing calendar, we will do a refresh after the resyncing is done.
					// So skip this refreshEvents here to avoid duplicated queries
					this.refreshEvents();
				}
			};

			refreshEvents = () => {
				this.onDateChange(this.state.dropdownDate, true, false);
			};

			getLastSyncDatetime = (): string => {
				if (!this.props.viewer.calendars?.edges) return "";

				return this.props.viewer.calendars.edges.reduce((lastSynced, edge) => {
					const lastSyncDatetime = edge?.node?.lastSyncDatetime;
					if (lastSyncDatetime && (!lastSynced || moment(lastSyncDatetime).isAfter(moment(lastSynced)))) {
						return lastSyncDatetime;
					} else {
						return lastSynced;
					}
				}, "");
			};

			onCalendarSyncStart = () => {
				this.setState({ isResyncing: true });
			};

			onCalendarSync = () => {
				this.props.relay?.refetch({}, null, () => {
					this.setState({ isResyncing: false }, this.refreshEvents);

					this.forceUpdate();
					this.setState({ lastSynced: this.getLastSyncDatetime() });
				});
			};

			onDateChangeCalendar = (date: Moment | Date) => {
				return this.onDateChange(date);
			};

			onDateChange = (date: Moment | Date, keepEvents = false, useCache?: boolean) => {
				// TODO: when state.start < date < state.end - probably we could just
				//  scroll to that day + do background request, without wiping events
				//  Need to think, how to scroll here, because this logic is inside AgendaViewWrapped
				//  maybe, forward ref, to pass agendaContent here
				const { viewMode } = this.state;
				const mdate = moment(date);
				const isDay = viewMode === "day";

				const start = isDay ? moment(mdate).startOf("day") : moment(mdate).subtract(PAGE_SIZE, "day");
				const end = isDay ? moment(mdate).endOf("day") : moment(mdate).add(PAGE_SIZE, "day");

				const canScroll = isDay ? false : mdate.isBetween(this.state.start, this.state.end, "day", "()");

				this.setState(
					prevState => ({
						start,
						end,
						prevButtonDisplayed: false,
						nextButtonDisplayed: false,
						current: mdate,
						dropdownDate: mdate,
						focused: false,
						loading: true,
						dayEvents: keepEvents || canScroll ? prevState.dayEvents : [],
					}),
					() => {
						this.loadEvents(useCache);
					},
				);
			};

			setDropdownDate = (date: Moment) => {
				if (date.format() === moment(this.state.dropdownDate).format()) return;

				this.setState({
					dropdownDate: date,
				});
			};

			loadNonCachedEvents = () => {
				this.loadEvents(false);
			};

			onDateFocus = (focused: boolean | null) => {
				if (focused !== null) this.setState({ focused });
			};

			onDateIncrease = () => {
				const pageSize = this.state.viewMode === "day" ? 1 : PAGE_SIZE;
				const date = moment(this.state.current).add(pageSize, "day");
				this.onDateChange(date);
			};

			onDateToday = () => {
				this.onDateChange(moment().startOf("day"));
			};

			onDateDecrease = () => {
				const pageSize = this.state.viewMode === "day" ? 1 : PAGE_SIZE;
				const date = moment(this.state.current).subtract(pageSize, "day");
				this.onDateChange(date);
			};

			onChangeEventFilters = () => {
				this.setState({ loading: true }, this.loadEventsWithoutCache);
			};

			onViewModeChange = (viewMode: string) => {
				if ((viewMode === "day" || viewMode === "agenda") && this.state.viewMode !== viewMode) {
					store.set(VIEW_MODE_KEY, viewMode);
					this.setState({ viewMode }, () => this.onDateChange(this.state.current, false, false));
				}
			};

			formatDate = (date: Date) => {
				return moment(date).format("h a");
			};

			formatTimeRange = ({ start, end }: { start: Date; end: Date }) => {
				const duration = moment(end).diff(moment(start), "minutes");
				return new Twix(moment(start), moment(end)).format({
					spaceBeforeMeridiem: true,
					hideDate: true,
					groupMeridiems: duration > 30,
				});
			};

			onEventFormSaved = (closeModal: () => void) => (result: EventFormResult) => {
				switch (result.type) {
					case "cancelled": {
						closeModal();
						return;
					}
					case "failed":
						return;
					case "succeeded": {
						if (result.event.eventGuid) {
							this.handleEventAdded(result.event.eventGuid, moment(result.event.start), false);
						}
						closeModal();
						return;
					}
					case "deleted": {
						this.handleEventDeleted(result.event.eventGuid);
						closeModal();
						return;
					}
				}
			};

			addEventButtonClicked = async () => {
				this.setState({
					selectedIntervalStart: undefined,
					selectedIntervalEnd: undefined,
				});

				const now = new Date();
				__globalModalContext?.open(({ close }) => (
					<EventFormModal
						viewer={this.props.viewer}
						onFormFinish={this.onEventFormSaved(close)}
						initialData={{
							start: set(startOfMinute(this.state.dropdownDate.toDate()), {
								hours: getHours(now),
								minutes: getMinutes(now),
							}),
						}}
					/>
				));
			};

			openEditEventModal = async (guid: string) => {
				const event = this.state.dayEvents.find(e => e.guid === guid);
				if (!event || !event.start || !event.end) return;

				__globalModalContext?.open(({ close }) => (
					<EventFormModal
						viewer={this.props.viewer}
						onFormFinish={this.onEventFormSaved(close)}
						eventToEdit={{
							guid: event.guid,
							title: event.title,
							// It's not okay if event.start/end are null but the check above should prevent that
							start: event.start ? new Date(event.start) : new Date(),
							end: event.end ? new Date(event.end) : new Date(),
							isAllDay: event.isAllDay,
							type: event.type,
							calendar: event.calendar,
						}}
					/>
				));
			};

			answerEventAttendance = (
				calendar: Calendar,
				eventGuid: string,
				userResponseStatus: ResponseStatusChoices,
				callback?: () => void,
			) => {
				const event = this.state.dayEvents.find(e => e.guid === eventGuid);
				if (!event) {
					return;
				}

				return this.handleEventAttendance(calendar, event, userResponseStatus, callback);
			};

			handleEventAttendance = async (
				calendar: Calendar,
				event: DayEvent,
				userResponseStatus: ResponseStatusChoices,
				callback?: () => void,
			) => {
				// TODO: temporary disabled for office - will assume auto "THIS" for that case
				if (event.recurrenceRule && calendar?.source !== "office365") {
					// Then it's occurrence, need to ask for edit type
					const choices: { value: EventEditType; label: string }[] = [
						{ value: "THIS", label: "This event" },
						{ value: "ALL", label: "All events" },
					];
					const result = await choiceModal({ choices, title: "RSVP to recurring event" });
					if (result) {
						this.sendAttendanceResponse(calendar, event, userResponseStatus, result.value as EventEditType, callback);
					}
				} else {
					this.sendAttendanceResponse(calendar, event, userResponseStatus, "THIS", callback);
				}
			};

			sendAttendanceResponse = (
				calendar: Calendar,
				event: DayEvent,
				userResponseStatus: ResponseStatusChoices,
				editType: EventEditType,
				callback?: () => void,
			) => {
				const { eventAttendanceMutationLoading } = this.state;
				if (!calendar || eventAttendanceMutationLoading) {
					return;
				}

				this.setState({ eventAttendanceMutationLoading: true });
				if (callback) callback();

				commitMutation<MeetingsPanelAttendanceMutation>(environment, {
					mutation: attendanceMutation,
					variables: {
						calendarId: calendar.id,
						guid: event.guid,
						userResponseStatus,
						editType,
					},
					onCompleted: () => {
						this.setState({ eventAttendanceMutationLoading: false });

						let answer = "";
						if (userResponseStatus === "accepted") {
							answer = "Yes";
						} else if (userResponseStatus === "declined") {
							answer = "No";
						} else if (userResponseStatus === "tentative" || userResponseStatus === "tentativelyAccepted") {
							answer = "Maybe";
						}

						toast.info(`Responded "${answer}" to "${event.title}"`);
					},
				});
			};

			private createNewMeeting = () => {
				if (this.props.createNewMeeting) {
					return this.props.createNewMeeting();
				}
				return this.createZoomMeeting();
			};

			private createZoomMeeting = async () => {
				let zoomMeetingUrl;
				try {
					zoomMeetingUrl = (await zoomSdk.getMeetingJoinUrl()).joinUrl;
				} catch (err) {
					// User might be on a version that don't have access to this Zoom API yet
					console.error("Failed to get Zoom's URL");
				}
				const calendars = this.getCalendars();
				const googleCalendar = calendars.find(c => c?.source === "google");
				const officeCalendar = calendars.find(c => c?.source === "office365");
				const activeCalendar = googleCalendar || officeCalendar || calendars[0];
				if (!activeCalendar) return;

				const start = moment();
				const end = moment().add(15, "minutes");
				const repeat = "NEVER";
				const title = "Zoom Call";
				const description = "Event automatically created from <a href='https://fellow.app'>Fellow's Zoom App</a>.";
				const isAllDay = false;
				const attendees: any[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any
				const optionalAttendees: any[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any
				const captchaToken = await recaptcha("add_event_modal");

				commitMutation<useSaveEventMutationMutation>(environment, {
					mutation: saveEventMutation,
					variables: {
						calendar: activeCalendar.id,
						start: start.format(),
						end: end.format(),
						repeat: repeat,
						title,
						description,
						isAllDay,
						attendees,
						optionalAttendees,
						captchaToken,
						conferenceCallUrl: zoomMeetingUrl,
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							toast.info(errors[0].message);
							return;
						}

						if (response.createOrUpdateEvent?.errors?.length) {
							for (let error of response.createOrUpdateEvent.errors) {
								if (error) {
									toast.info(error.messages[0]);
								}
							}
						}

						const guid = response.createOrUpdateEvent?.guid;
						const newStart = response.createOrUpdateEvent?.start;
						if (guid && newStart) {
							this.handleEventAdded(guid, moment(newStart), true);
						}
					},
					onError: error => {
						toast.info(error.message);
					},
				});
			};

			// Prevent slot selection
			preventSelecting = () => false;

			handleSlotSelected = async ({
				action,
				start,
				end,
			}: {
				action: string;
				start: string | Date;
				end: string | Date;
			}) => {
				// We only want to do something on click of timeslots, so we can deselect.
				if (action === "doubleClick") {
					this.handleEventSelected();
					this.props.history.push(`/meetings/`);
				} else if (action === "select") {
					this.setState({
						selectedIntervalStart: moment(start),
						selectedIntervalEnd: moment(end),
					});

					let chosenTime: { start: Date; end: Date } | undefined = undefined;
					if (this.state.selectedIntervalStart && this.state.selectedIntervalEnd) {
						chosenTime = {
							start: this.state.selectedIntervalStart.toDate(),
							end: this.state.selectedIntervalEnd.toDate(),
						};
					}

					__globalModalContext?.open(({ close }) => (
						<EventFormModal
							viewer={this.props.viewer}
							onFormFinish={this.onEventFormSaved(close)}
							initialData={chosenTime}
						/>
					));
				}
			};

			handleEventSelected = () => {
				if (inGoogleMeet) {
					dispatchAnalyticsTrack("[Embedded Meetings] Event selected in Google Meet");
				}
				perf.startSwitchingStreams("meetings");
				this.setState({ scrollToMeeting: false });
			};

			handleEventAdded = (guid: string, start: Moment, fromZoom: boolean) => {
				window.relayCache?.clear();
				this.loadEvents(false);
				this.props.onEventsChanged?.();
				this.onDateChange(moment(start).startOf("day"));
				this.handleEventSelected();
				this.props.history.push(`/meetings/${guid}/`);

				if (fromZoom) {
					const content = (
						<div>
							A new event has been added to your calendar, click here to invite others on this call to collaborate on
							the note!
						</div>
					);
					toast.success(content, {
						onClick: () => {
							this.openEditEventModal(guid);
						},
					});
				}
			};

			handleEventDeleted = (guid: string) => {
				this.props.onEventsChanged?.();
				this.loadEvents(false);
				const eventGuid = this.getEventGuid();
				if (eventGuid === guid) {
					this.props.history.push("/meetings/");
				}
			};

			setPrevButtonDisplayed = (val: boolean) => {
				this.setState({ prevButtonDisplayed: val });
			};

			setNextButtonDisplayed = (val: boolean) => {
				this.setState({ nextButtonDisplayed: val });
			};

			eventPropGetter = (e: DayEvent) => {
				const eventGuid = this.getEventGuid();
				return {
					className: cx(`status-${e.userResponseStatus}`, {
						"rbc-selected": e.guid === eventGuid,
						"has-note": e.hasNoteContent,
						"is-cancelled": e.status === "cancelled",
					}),
				};
			};

			getScrollToTime = (earliestEventTime: Moment) => {
				let dt;
				const eventGuid = this.getEventGuid();
				const selected = this.state.dayEvents.find(e => e.guid === eventGuid) || null;
				if (this.state.scrollToMeeting && !!selected?.start && moment(selected.start).isSame(this.state.start, "day")) {
					dt = moment(selected.start).startOf("hour");
				} else if (moment().isSame(this.state.start, "day")) {
					dt = moment().startOf("hour");
				} else {
					dt = moment(this.getMinDisplayTime(earliestEventTime));
				}

				const offsetFromMin = Math.max(
					0,
					moment.duration(dt.subtract(3, "hours").diff(moment(this.getMinDisplayTime(earliestEventTime)))).hours(),
				);
				return new Date(1970, 1, 1, offsetFromMin);
			};

			getMinDisplayTime = (earliestEventTime: Moment) => {
				const eightAM = moment(this.state.start).set({ h: 8, m: 0, s: 0 });
				if (earliestEventTime && earliestEventTime.isBefore(eightAM)) {
					const midnight = moment(this.state.start).set({ h: 0, m: 0, s: 0 });
					const beforeEarliestEventTime = earliestEventTime.startOf("hour").subtract(1, "hour");

					return moment.max(midnight, beforeEarliestEventTime).toDate();
				} else {
					return eightAM.toDate();
				}
			};

			getMaxDisplayTime = (latestEventTime: Moment) => {
				const sixPM = moment(this.state.start).set({ h: 18, m: 0, s: 0 });
				if (latestEventTime && latestEventTime.isAfter(sixPM)) {
					const midnight = moment(this.state.start).set({ h: 23, m: 59, s: 59 });
					const afterLatestEventTime = latestEventTime.add(1, "hours").subtract(1, "second");

					return moment.min(midnight, afterLatestEventTime).toDate();
				} else {
					return sixPM.subtract(1, "second").toDate();
				}
			};

			getNow = () => {
				return this.state.now.startOf("minute").toDate();
			};

			loadEventsWithoutCache = () => {
				this.loadEvents(false);
			};

			loadEvents = (useCache = true) => {
				const { start, end } = this.state;

				const variables = {
					start: moment(start).toISOString(),
					end: moment(end).toISOString(),
				};
				queryRetainer.retain(ConcreteMeetingsPanelQuery, variables);
				return fetchQuery<MeetingsPanelQueryOperation>(environment, MeetingsPanelQuery, variables, {
					fetchPolicy: useCache ? "store-or-network" : "network-only",
				})
					.toPromise()
					.then(response => {
						let dayEvents = (response?.viewer.dayEvents.edges.map(edge => edge && edge.node).filter(e => e) ??
							[]) as DayEvent[];
						dayEvents = dayEvents.map(ev => {
							if (!ev.isAllDay) {
								return ev;
							}

							return {
								...ev,
								start: moment(ev.start || undefined)
									.startOf("day")
									.format(),
								end: moment(ev.end || undefined)
									.startOf("day")
									.format(),
							};
						});
						this.setState({
							dayEvents,
							loading: false,
							prevLoading: false,
							nextLoading: false,
						});
					})
					.catch(() => {
						this.setState({
							loading: false,
							prevLoading: false,
							nextLoading: false,
							dayEvents: [],
						});
					});
			};

			previousPage = () => {
				const { viewMode, prevLoading, loading, nextLoading } = this.state;
				if (prevLoading || loading || nextLoading || viewMode !== "agenda") {
					return;
				}

				const current = moment(this.state.current).subtract(PAGE_SIZE, "day").startOf("day");
				const start = moment(current).subtract(PAGE_SIZE, "day");
				const end = moment(current).add(PAGE_SIZE, "day");
				this.setState(
					{
						prevLoading: true,
						prevButtonDisplayed: false,
						nextButtonDisplayed: false,
						start,
						current,
						end,
					},
					this.loadEvents,
				);
			};

			nextPage = () => {
				const { viewMode, prevLoading, loading, nextLoading } = this.state;
				if (prevLoading || loading || nextLoading || viewMode !== "agenda") {
					return;
				}

				const current = moment(this.state.current).add(PAGE_SIZE, "day").startOf("day");
				const start = moment(current).subtract(PAGE_SIZE, "day");
				const end = moment(current).add(PAGE_SIZE, "day");
				this.setState(
					{
						nextLoading: true,
						prevButtonDisplayed: false,
						nextButtonDisplayed: false,
						start,
						current,
						end,
					},
					this.loadEvents,
				);
			};

			getCalendars = () => {
				return (this.props.viewer.calendars?.edges.filter(e => !!e).map(e => e?.node) ?? []) as Calendar[];
			};

			openZoomOnboardingPopup = () => {
				this.setState({ zoomCreateEventTooltipOpen: true });
				document.addEventListener("click", this.closeZoomCreateEventTooltip);
			};

			closeZoomCreateEventTooltip = () => {
				document.removeEventListener("click", this.closeZoomCreateEventTooltip);
				this.setState({ zoomCreateEventTooltipOpen: false });
				dismissElement(ZOOM_TOOLTIP);
			};

			_prevContextValue?: AgendaContextType;
			getContextValue() {
				const { visible } = this.props;
				const { loading, prevLoading, nextLoading, prevButtonDisplayed, nextButtonDisplayed } = this.state;
				const calendars = this.getCalendars();

				const wrapperProps = {
					currentDate: this.state.current.toDate(),
					dropdownDate: this.state.dropdownDate.toDate(),
					loading,
					visible,
					prevLoading,
					nextLoading,
					prevButtonDisplayed,
					nextButtonDisplayed,
					setPrevButtonDisplayed: this.setPrevButtonDisplayed,
					setNextButtonDisplayed: this.setNextButtonDisplayed,
					onTopScrolled: this.previousPage,
					onBottomScrolled: this.nextPage,
					onDateScrolled: this.setDropdownDate,
					isDarkMode: false,
					context: this.context,
				};

				const value = {
					uxSimplificationEnabled: this.props.viewer.uxSimplificationEnabled,
					refreshEvents: this.refreshEvents,
					openEditModal: this.openEditEventModal,
					answerEventAttendance: this.answerEventAttendance,
					answerEventAttendanceLoading: this.state.eventAttendanceMutationLoading,
					calendars,
					...wrapperProps,
				};

				if (!this._prevContextValue || !isEqual(this._prevContextValue, value)) {
					this._prevContextValue = value;
				}
				return this._prevContextValue;
			}

			render() {
				const { dayEvents, current } = this.state;
				const { isMobile } = this.context;
				const allowDragNDrop = !isMobile || (isMobile && inZoomMeeting);
				const startOfDate = moment(current).startOf("day");
				const endOfDate = moment(current).endOf("day");
				let earliestEventTime = endOfDate;
				let latestEventTime = startOfDate;

				let hasAllDayEvents = false;

				const calendars = this.getCalendars();
				const shouldDisplayNoCalendarMessage = !calendars.find(c => c.source !== "internal");

				dayEvents.forEach(node => {
					if (node.isAllDay) {
						hasAllDayEvents = true;
						return;
					}

					const eventStart = node.start ? moment(node.start) : moment();
					if (moment(eventStart).startOf("day").isSame(startOfDate) && eventStart < earliestEventTime) {
						earliestEventTime = eventStart;
					}

					const eventEnd = node.end ? moment(node.end) : moment();
					if (moment(eventEnd).endOf("day").isSame(endOfDate) && eventEnd > latestEventTime) {
						latestEventTime = eventEnd;
					}
				});

				const isOnCalendarPage = this.props.history.location.pathname.startsWith("/calendar");

				return (
					<section className={cx("agenda", panelStyles.panelBase)} id="agenda-container">
						{inVideocallMode ? (
							<header>
								<div className="agenda-header">
									<Popup
										inverted
										open={this.state.zoomCreateEventTooltipOpen}
										size="tiny"
										content={
											<div className={styles.dismissablePopup}>
												<FontAwesomeIcon
													icon={faTimes}
													className="zoomClosePopup"
													onClick={this.closeZoomCreateEventTooltip}
												/>
												<span>
													<b>Unscheduled call? No problem!</b>
													<br />
													Click here to create a new event and jump right into taking notes.
												</span>
											</div>
										}
										position="bottom center"
										trigger={
											<Button variant="primary" onClick={this.createNewMeeting} className="createZoomMeeting">
												<FontAwesomeIcon className="icon" icon={faPlus} />{" "}
												{this.props.viewer.hasAccessToCopilot ? "Take AI notes" : "Start a new meeting"}
											</Button>
										}
										className="zoomPopup"
									/>
								</div>
							</header>
						) : (
							<MeetingsPanelHeader
								viewer={this.props.viewer}
								visible={this.props.visible}
								current={this.state.current}
								onDateChange={this.onDateChange}
								onDateToday={this.onDateToday}
								dropdownDate={this.state.dropdownDate.toDate()}
								focused={this.state.focused}
								onDateFocus={this.onDateFocus}
								viewMode={this.state.viewMode}
								changeViewMode={this.onViewModeChange}
								closeGoogleCalendarPanel={closeGoogleCalendarPanel}
								openEventCreationModal={this.addEventButtonClicked}
							/>
						)}
						{!inCalendarMode && !inVideocallMode && <OpenWeekViewButton currentDate={this.state.current.toDate()} />}
						<PanelMessage
							start={this.state.start}
							viewMode={this.state.viewMode}
							loading={this.state.loading}
							dayEvents={this.state.dayEvents}
							noExternalCalendars={shouldDisplayNoCalendarMessage}
						/>
						<EventCalendar
							context={this.getContextValue()}
							eventPropGetter={this.eventPropGetter}
							className={cx({
								"agenda-view": this.state.viewMode === "agenda",
								"has-all-day-events": hasAllDayEvents,
							})}
							events={dayEvents}
							date={this.state.start.toDate()}
							onNavigate={this.onDateChangeCalendar}
							localizer={localizer}
							toolbar={false}
							getNow={this.getNow}
							min={this.getMinDisplayTime(earliestEventTime)}
							max={this.getMaxDisplayTime(latestEventTime)}
							selected={this.getEventGuid()}
							selectable={this.state.viewMode === "day" && !allowDragNDrop ? true : "ignoreEvents"}
							onSelectEvent={this.handleEventSelected}
							onSelecting={this.state.viewMode === "agenda" || !allowDragNDrop ? this.preventSelecting : undefined}
							onSelectSlot={this.handleSlotSelected}
							scrollToTime={this.getScrollToTime(earliestEventTime)}
							formats={{
								timeGutterFormat: this.formatDate,
								eventTimeRangeFormat: this.formatTimeRange,
							}}
							view={this.state.viewMode as "agenda" | "day"}
							onView={noop}
							views={{
								day: DayView,
								agenda: AgendaView,
							}}
							length={PAGE_SIZE * 2 + 1}
							components={{
								eventWrapper: this.state.viewMode === "day" ? DayEventContent : undefined,
								timeSlotWrapper: TimeSlotWrapper({ earliestEventTime, latestEventTime }) as FunctionComponent,
							}}
						/>
						<div className={cx({ "calendar-dimmer": this.state.focused })} />

						{!isOnCalendarPage && (
							<ResyncCalendar
								resyncRef={this.resyncRef}
								afterSync={this.onCalendarSync}
								currentDate={this.state.current.toDate()}
								lastSynced={this.state.lastSynced}
								calendarServiceToReconnect={this.props.viewer.calendarServiceToReconnect}
								makeWayForReturnToMeetingPrompt
							/>
						)}
					</section>
				);
			}
		},
	),
);
